import React from "react";

import { hasPermission, permissionSetFromSession } from "@auto/utils/permissions";
import { getLocale, t } from "@auto/services";

import manifest from "@src/doc/api-manifest.json";
import documentationBundle from "@src/doc/api-docs.json";

import { ApiEndpointCard } from "./ApiEndpointCard";
import styles from "./ApiDocumentationPage.module.styl";

const endpointEntries = Object.entries(manifest.endpoints || {});
const endpointById = new Map(endpointEntries);

const methodClass = (method) => {
  const value = String(method || "").toUpperCase();
  if (value === "GET") return styles.methodGet;
  if (value === "POST") return styles.methodPost;
  if (value === "PUT") return styles.methodPut;
  if (value === "PATCH") return styles.methodPatch;
  if (value === "DELETE") return styles.methodDelete;
  return styles.methodOther;
};

import { MarkdownText } from "./MarkdownText";

const EndpointLink = ({ endpointId, selected, onSelect }) => {
  const endpoint = endpointById.get(endpointId);
  if (!endpoint) {
    throw new Error(`Documentation references unknown endpoint ${endpointId}`);
  }

  return (
    <button
      type="button"
      className={selected ? styles.endpointLinkActive : styles.endpointLink}
      onClick={() => onSelect(endpointId)}
    >
      <span className={`${styles.methodBadge} ${methodClass(endpoint.httpMethod)}`}>
        {endpoint.httpMethod}
      </span>
      <span className={styles.endpointPath}>{endpoint.path}</span>
    </button>
  );
};

const GuideView = ({ documentation, onSelectEndpoint }) => (
  <div className={styles.guide}>
    <div className={styles.pageHeader}>
      <div className={styles.eyebrow}>{t("developer.apiDocs.developerDocumentation")}</div>
      <h1>{documentation.title}</h1>
      <MarkdownText text={documentation.introduction} />
    </div>

    {(documentation.chapters || []).map((chapter) => (
      <section key={chapter.id} id={`chapter-${chapter.id}`} className={styles.chapter}>
        <div className={styles.chapterHeader}>
          <h2>{chapter.title}</h2>
          <MarkdownText text={chapter.introduction} />
        </div>

        {(chapter.sections || []).map((section) => (
          <div key={section.id} id={`section-${section.id}`} className={styles.section}>
            <h3>{section.title}</h3>
            {section.content ? <MarkdownText text={section.content} /> : null}

            {section.endpoints?.length ? (
              <div className={styles.sectionEndpoints}>
                {section.endpoints.map((endpointId) => (
                  <EndpointLink
                    key={endpointId}
                    endpointId={endpointId}
                    selected={false}
                    onSelect={onSelectEndpoint}
                  />
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </section>
    ))}
  </div>
);

const ReferenceIndex = ({ documentation, query, onSelectEndpoint }) => {
  const normalized = query.trim().toLowerCase();

  const visible = endpointEntries.filter(([endpointId, endpoint]) => {
    if (!normalized) return true;
    const haystack = [
      endpointId,
      endpoint.httpMethod,
      endpoint.path,
      endpoint.name,
      endpoint.serviceName,
      endpoint.apiName,
      endpoint.sourceComponentName,
      endpoint.permission,
      documentation.endpoints?.[endpointId]?.title,
      documentation.endpoints?.[endpointId]?.summary,
    ].filter(Boolean).join(" ").toLowerCase();
    return haystack.includes(normalized);
  });

  const grouped = new Map();
  for (const [endpointId, endpoint] of visible) {
    const serviceKey = endpoint.serviceKey;
    if (!grouped.has(serviceKey)) {
      grouped.set(serviceKey, {
        name: endpoint.serviceName,
        source: endpoint.sourceComponentName,
        endpoints: [],
      });
    }
    grouped.get(serviceKey).endpoints.push([endpointId, endpoint]);
  }

  return (
    <div className={styles.referenceIndex}>
      {Array.from(grouped.entries()).map(([serviceKey, group]) => (
        <div key={serviceKey} className={styles.referenceGroup}>
          <div className={styles.referenceGroupHeader}>
            <div>{group.name}</div>
            <span>{group.source}</span>
          </div>
          {group.endpoints.map(([endpointId]) => (
            <EndpointLink
              key={endpointId}
              endpointId={endpointId}
              selected={false}
              onSelect={onSelectEndpoint}
            />
          ))}
        </div>
      ))}

      {!visible.length ? (
        <div className={styles.emptyState}>{t("developer.apiDocs.noEndpointsMatch")}</div>
      ) : null}
    </div>
  );
};

const permissionDenied = (requiredPermission) => (
  <div className={styles.accessDenied}>
    <div className={styles.accessDeniedCode}>403</div>
    <h1>{t("developer.apiDocs.accessDenied")}</h1>
    <p>{t("developer.apiDocs.pageRequires")} <code>{requiredPermission}</code>.</p>
  </div>
);

const AuthorizedApiDocumentationPage = ({ gui, session, documentation }) => {
  const initialEndpoint = new URLSearchParams(window.location.search).get("endpoint");
  if (initialEndpoint && !endpointById.has(initialEndpoint)) {
    throw new Error(`Unknown endpoint in URL: ${initialEndpoint}`);
  }

  const [selectedEndpointId, setSelectedEndpointId] = React.useState(initialEndpoint || "");
  const [search, setSearch] = React.useState("");
  const [sidebarMode, setSidebarMode] = React.useState(initialEndpoint ? "reference" : "guide");

  const selectEndpoint = React.useCallback((endpointId) => {
    if (!endpointById.has(endpointId)) {
      throw new Error(`Unknown endpoint ${endpointId}`);
    }

    const url = new URL(window.location.href);
    url.searchParams.set("endpoint", endpointId);
    window.history.replaceState({}, "", url.toString());
    setSelectedEndpointId(endpointId);
    setSidebarMode("reference");
  }, []);

  const showGuide = React.useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete("endpoint");
    window.history.replaceState({}, "", url.toString());
    setSelectedEndpointId("");
    setSidebarMode("guide");
  }, []);

  const selectedEndpoint = selectedEndpointId
    ? endpointById.get(selectedEndpointId)
    : null;
  const selectedDocumentation = selectedEndpointId
    ? documentation.endpoints?.[selectedEndpointId]
    : null;

  if (selectedEndpoint && !selectedDocumentation) {
    throw new Error(`api-docs.json is missing documentation for ${selectedEndpointId}`);
  }

  return (
    <div className={styles.shell}>
      <aside className={styles.sidebar}>
        <div className={styles.sidebarTop}>
          <button type="button" className={styles.docsHome} onClick={showGuide}>
            <span className={styles.docsHomeTitle}>{t("developer.apiDocs.title")}</span>
            <span className={styles.docsHomeSubtitle}>{manifest.rwebComponentName}</span>
          </button>

          <div className={styles.modeTabs}>
            <button
              type="button"
              className={sidebarMode === "guide" ? styles.modeTabActive : styles.modeTab}
              onClick={() => setSidebarMode("guide")}
            >
              {t("developer.apiDocs.guide")}
            </button>
            <button
              type="button"
              className={sidebarMode === "reference" ? styles.modeTabActive : styles.modeTab}
              onClick={() => setSidebarMode("reference")}
            >
              {t("developer.apiDocs.reference")}
            </button>
          </div>

          {sidebarMode === "reference" ? (
            <input
              className={styles.search}
              type="search"
              value={search}
              placeholder={t("developer.apiDocs.searchEndpoints")}
              onChange={(event) => setSearch(event.target.value)}
            />
          ) : null}
        </div>

        <div className={styles.sidebarBody}>
          {sidebarMode === "guide" ? (
            <nav className={styles.guideNav}>
              {(documentation.chapters || []).map((chapter) => (
                <div key={chapter.id} className={styles.guideNavChapter}>
                  <a href={`#chapter-${chapter.id}`}>{chapter.title}</a>
                  {(chapter.sections || []).map((section) => (
                    <a key={section.id} href={`#section-${section.id}`} className={styles.guideNavSection}>
                      {section.title}
                    </a>
                  ))}
                </div>
              ))}
            </nav>
          ) : (
            <ReferenceIndex documentation={documentation} query={search} onSelectEndpoint={selectEndpoint} />
          )}
        </div>
      </aside>

      <main className={styles.main}>
        {selectedEndpoint ? (
          <ApiEndpointCard
            endpoint={selectedEndpoint}
            documentation={selectedDocumentation}
            session={session}
            onBack={showGuide}
          />
        ) : (
          <GuideView documentation={documentation} onSelectEndpoint={selectEndpoint} />
        )}
      </main>
    </div>
  );
};

export const ApiDocumentationPage = ({ gui }) => {
  const session = gui?.props?.session;
  const permissions = permissionSetFromSession(session);
  const requiredPermission = manifest.requiredPermission;
  const locale = getLocale();

  if (!requiredPermission) {
    throw new Error("api-manifest.json is missing requiredPermission");
  }

  if (!locale) {
    throw new Error("API documentation requires an active locale");
  }

  const documentation = documentationBundle.locales?.[locale];
  if (!documentation) {
    throw new Error(`api-docs.json is missing documentation locale ${locale}`);
  }

  if (!hasPermission(permissions, requiredPermission)) {
    return permissionDenied(requiredPermission);
  }

  return (
    <AuthorizedApiDocumentationPage
      gui={gui}
      session={session}
      documentation={documentation}
    />
  );
};

export const displayApiDocumentation = (gui) => ({
  view: () => <ApiDocumentationPage gui={gui} />,
});

export default ApiDocumentationPage;
